import { PaginationState } from "@tanstack/react-table";
import classNames from "classnames";
import { format } from "date-fns";
import { Field, Formik, Form } from "formik";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SkeletonTheme } from "react-loading-skeleton";
import { useLocation, useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import ModalButton from "../../shared/Ui/Buttons/ModalButtons";
import Calendar from "../../shared/Ui/Calendar/Calendar";
import CheckBox from "../../shared/Ui/CheckBox/CheckBox";
import * as Yup from "yup";
import { EmptyListPlaceholderContent } from "../../shared/Ui/EmptyPageBlock";
import ModalComponent from "../../shared/Ui/ModalComponent/ModalWindow";
import SelectCustomFormik from "../../shared/Ui/SelectCustom/SelectCustomFormik";
import StatusBadge from "../../shared/Ui/StatusBadge/StatusBadge";
import ActionDropwdownCell from "../../shared/components/Table/ActionDropwdownCell";
import Pagination from "../../shared/components/Table/Pagination";
import { ITable } from "../../shared/components/Table/types";
import { useAppDispatch, useAppSelector } from "../../shared/hook";
import style from "./index.module.css";
import MockSkeletonDocs from "./utils/MockSkeletonDocs";
import TableLoader from "../../shared/Ui/Loader/TableLoader";
import PageNotRole from "../../shared/Ui/PageNotFound/PageNotRole";
import Loader from "../../shared/Ui/Loader/Loader";
import SearchIcon from './../../../public/icons/icon-search.svg'
import CalendarSelectFormik from "../../shared/Ui/Calendar/ CalendarSelectFormik";
import { getCheck, getConsumerList, getErrorCheck, getErrorCheckList, getOfdList, resendCheckList } from "../../common/api/checkApi";
import { check } from "prettier";
import { FaRegCopy, FaExclamationCircle } from "react-icons/fa";
import { IoCheckmarkOutline } from "react-icons/io5";
import styles from "react-day-picker/dist/style.css";
import { ta } from "date-fns/locale";
import { table } from "console";
export type DocsType = "INBOX" | "SENT" | "DRAFT" | "PERSONALIZATION";

interface IDocumentType {
  type: DocsType;
}

interface StatusType {
  type: string;
  label: string;
}

interface LooseObject {
  [key: string]: any;
}
type IdType = number | string; // Определите тип данных для ID
type DocumentID = string | number; // Определяем тип для ID документов

export const DocumentList: React.FC<any> = ({ type }) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [failedModal, setFailedModal] = useState<DocumentID[]>([]);
  const [activeTooltipId, setActiveTooltipId] = useState<number | null>(null);
  const handleTooltipToggle = (id: number) => {
    setActiveTooltipId((prevId) => (prevId === id ? null : id));
  };
  const handleOutsideClick = (event: MouseEvent) => {
    console.log(337);
    if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
      setActiveTooltipId(null);
    }
  };
  const [sort, setSort] = useState<"ASC" | "DESC">("DESC");
  const [resendArray, setResendArray] = useState<any>([]);
  const { t, i18n } = useTranslation();
  const [tableData, setTableData] = useState<any>();
  const pageSize = useAppSelector((state) => state.userSettings.pageSize);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSize,
  });
  const navigate = useNavigate();
  const [dataObject, setDataObject] = useState<any>({
    startDate: undefined, // Начальная дата по умолчанию
    startTime: "", // Время для начальной даты по умолчанию
    endDate: undefined, // Конечная дата по умолчанию
    endTime: "", // Время для конечной даты по умолчанию
  });
  const [copied, setCopied] = useState<boolean>(false)
  const [isModalOpen, setIsModalOpen] = useState<string | null>(null);
  const [errorModal, setErrorModal] = useState<any>(null);
  const onModalClose = () => setIsModalOpen(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isComplexSearch, setIsComplexSearch] = useState(false);
  const [checkModal, setCheckModal] = useState<any>(null);
  const [consumerList, setConsumerList] = useState([]);
  const [ofdList, setOfdList] = useState([]);
  const [initialValues, setInitialValues] = useState({
    ofdId: null,
    fromDateTime: null,
    toDateTime: null,
    searchTerm: '',
    apiVersion: null,
    sourceType: null,
  });

  const statusTypes: { [key: string]: StatusType } = {
    SENT: { type: "active", label: t("statusTypes.sent") }, // Зеленый
    ERROR: { type: "error", label: t("statusTypes.no_sent") }, // Красный
    SKIPPED: { type: "test", label: t("statusTypes.skipped") }, // Желтый
    REPROCESSING: { type: "processing", label: t("statusTypes.processing") }, // Голубой
  };

  const formValidationSchema = () =>
    Yup.object().shape({
      searchTerm: Yup.string()
        .min(12, 'Минимум 12 символов'),
    });

  const handleCopied = () => {
    setCopied(true)
    setTimeout(() => {
      setCopied(false)
    }, 2000)
  }

  const versionDictionary: { [key: string]: string } = {
    'V_2_0_3': '1.0.3',
    'V_2_0_4': '1.0.4',
    'V_1_0_3': '1.0.3',
    'V_1_0_4': '1.0.4',
  };

  const formatApiVersion = (apiVersion: string | null | undefined): string => {
    if (!apiVersion) return "-";
    if (versionDictionary[apiVersion]) {
      return versionDictionary[apiVersion];
    }
    // Если версии нет в словаре, используем стандартное преобразование
    return apiVersion.replace(/V_/g, '').replace(/_/g, '.');
  }

  // Бэкенд уже присылает время с учётом часового пояса — форматируем в UTC, чтобы не дублировать смещение
  const formatDateTime = (timestamp: number | string | null | undefined): string => {
    try {
      if (!timestamp) return (timestamp as string) || "";
      const numTimestamp = typeof timestamp === "string" ? parseInt(timestamp, 10) : timestamp;
      if (isNaN(numTimestamp) || numTimestamp <= 0) return String(timestamp);
      const date = new Date(numTimestamp * 1000);
      if (isNaN(date.getTime())) return String(timestamp);
      const d = date.getUTCDate().toString().padStart(2, "0");
      const M = (date.getUTCMonth() + 1).toString().padStart(2, "0");
      const y = date.getUTCFullYear();
      const h = date.getUTCHours().toString().padStart(2, "0");
      const m = date.getUTCMinutes().toString().padStart(2, "0");
      const s = date.getUTCSeconds().toString().padStart(2, "0");
      return `${d}.${M}.${y}, ${h}:${m}:${s}`;
    } catch (error) {
      console.error("Ошибка форматирования dateTime:", error);
      return String(timestamp || "");
    }
  };

  const formatCheckDataForDisplay = (data: any): any => {
    if (!data) return data;
    try {
      const formatted = JSON.parse(JSON.stringify(data)); // Глубокое копирование
      
      // Добавляем форматированное время как новое поле сразу после dateTime
      if (formatted.receipt?.dateTime) {
        const receipt = formatted.receipt;
        const dateTimeValue = receipt.dateTime;
        const dateTimeFormatted = formatDateTime(dateTimeValue);
        
        // Создаем новый объект с правильным порядком полей
        const newReceipt: any = {};
        Object.keys(receipt).forEach(key => {
          newReceipt[key] = receipt[key];
          // После dateTime добавляем dateTimeFormatted
          if (key === 'dateTime') {
            newReceipt.dateTimeFormatted = dateTimeFormatted;
          }
        });
        
        formatted.receipt = newReceipt;
      }
      
      return formatted;
    } catch (error) {
      console.error("Ошибка форматирования данных чека:", error);
      return data;
    }
  }

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const fetchCheck = async () => {
    const requestData: LooseObject = {};

    if (initialValues?.searchTerm === '') {
      requestData.searchTerm = null;
    } else {
      requestData.searchTerm = initialValues?.searchTerm.trim().replace(/\s+/g, '');
    }

    if (initialValues?.ofdId !== null) {
      requestData.ofdId = initialValues?.ofdId;
    }

    if (initialValues?.apiVersion !== null) {
      const apiVersionValue = initialValues.apiVersion;
      requestData.apiVersion = (apiVersionValue && typeof apiVersionValue === 'object' && 'value' in apiVersionValue)
        ? (apiVersionValue as any).value 
        : apiVersionValue;
    }

    if (initialValues?.sourceType !== null) {
      const sourceTypeValue = initialValues.sourceType;
      requestData.sourceType = (sourceTypeValue && typeof sourceTypeValue === 'object' && 'value' in sourceTypeValue)
        ? (sourceTypeValue as any).value 
        : sourceTypeValue;
    }

    if (dataObject.startDate) {
      // Формируем fromDateTime
      const startDate = dataObject.startDate;
      const startTime = dataObject.startTime || "00:00:00";
      requestData.fromDateTime = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}T${startTime}`;

      // Формируем toDateTime
      if (dataObject.endDate) {
        const endDate = dataObject.endDate;
        const endTime = dataObject.endTime || "23:59:59";
        requestData.toDateTime = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}T${endTime}`;
      } else {
        requestData.toDateTime = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}T${dataObject.endTime}`; // Если endDate отсутствует
      }
    }

    setIsLoading(true);
    try {
      const data = await getErrorCheckList(
        pagination.pageIndex + 1,
        pagination.pageSize,
        requestData,
      );
      setTableData(data);
    } catch (error) {
      console.error(t("checks.getData_error"), error);
    } finally {
      setIsLoading(false);
    }
  };

  // async function renderConsumerList() {
  //   await getConsumerList()
  //     .then((response: any) => {
  //       const options =
  //         response &&
  //         response?.consumers?.map((item: any) => ({
  //           value: item.id,
  //           label: item.name,
  //         }));
  //       setConsumerList(options);
  //     })
  // }

  // async function renderOfdList() {
  //   await getOfdList()
  //     .then((response: any) => {
  //       const options =
  //         response &&
  //         response?.ofds?.map((item: any) => ({
  //           value: item.id,
  //           label: item.name,
  //         }));
  //       setOfdList(options);
  //     })
  // }

  useEffect(() => {

    fetchCheck();

    getOfdList('', 1, 10000)
      .then((response: any) => {
        const options =
          response &&
          response?.ofds?.map((item: any) => ({
            value: item.id,
            label: item.name,
          }));
        setOfdList(options);
      })

    getConsumerList(1, 10000)
      .then((response: any) => {
        const options =
          response &&
          response?.consumers?.map((item: any) => ({
            value: item.id,
            label: item.name,
          }));
        setConsumerList(options);
      })
  }, [pagination]);


  const renderActions = (
    value: any,
    setErrorModal: any,
    navigate: any,
    setAlertOpen?: any,
    setIsCheckOpen?: any,
  ) => {
    const actions = [];
    actions.push({
      title: t("checks.show"),
      action: () => {
        getErrorCheck(value.id).then((response) => {
          setCheckModal(response);
        });
        setActiveTooltipId(null);
        // navigate("/cheque/" + value?.id)
      }
    });
    {
      
      // (actions.push({
      //   title: t("checks.resend"),
      //   action: () => {
      //     resendCheckList([value.id])
      //   }
      // }))
  }
    return actions;
  };


  const calculateItemNumber = (index: any) => {
    return pagination.pageIndex * pagination.pageSize + index + 1;
  };


  const toggleSort = () => {
    const newSortDirection = sort === "ASC" ? "DESC" : "ASC";
    setSort(newSortDirection);
  };

  const checkBoxResend = (id: any) => {
    setResendArray((prevResendArray: any) => {
      if (prevResendArray.includes(id)) {
        return prevResendArray.filter((item: any) => item !== id);
      } else {
        return [...prevResendArray, id];
      }
    });
  };

  const fullResendMarks = () => {
    setResendArray([]);
    if (tableData?.checks) {
      const allIds = tableData.checks.map((value: any) => value?.id);
      setResendArray(allIds);
    }
    if (resendArray?.length === tableData?.checks?.length) {
      setResendArray([]);
    }
  };

  // if (!role && isLoadingRole) return <Loader />;

  // if (!role || !role?.view) {
  //   return (
  //     <PageNotRole />
  //   )
  // }

  return (
    <div className="flex flex-1 flex-col min-w-0">
      <h2 className="my-3 text-xl font-semibold">
        {t("checks.automatic_workspace")}
      </h2>

      <div className="bg-white w-full  my-5 p-5  pt-8 relative rounded-xl shadow-md flex flex-wrap flex-row align-center gap-[20px]">
        <Formik
          initialValues={initialValues}
          onSubmit={() => {
            setPagination((prevValues: PaginationState) => {
              return { ...prevValues, pageIndex: 0 };
            });
          }}
          // enableReinitialize={true}
          validateOnBlur={true}
          validateOnChange={true}
          validationSchema={formValidationSchema}

        >
          {(formik) => (
            <Form className="w-full">
              <div className="flex flex-1 justify-between">
                <div className="flex flex-row w-full gap-[10px]">

                  <div className="relative w-full max-w-[500px]">

                    <div className="flex flex-col gap-1">
                      {formik.touched.searchTerm && formik.errors.searchTerm && (
                        <div className={style.tooltipWrapper22}>
                          <FaExclamationCircle className={style.errorIcon} />
                          <div className={style.tooltipText}>
                            {typeof formik.errors.searchTerm === "string"
                              ? formik.errors.searchTerm
                              : t("checks.error")}
                          </div>
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                      </div>
                      <div className="relative">
                        <img
                          src="/icons/icon-search.svg"
                          alt="Search"
                          className="absolute left-2 top-1/2 transform -translate-y-1/2 w-5 h-5"
                        />
                        <Field
                          type="text"
                          name="searchTerm"
                          placeholder={t("checks.placeholder")}
                          maxLength={150}
                          className="w-full h-10 border border-mainColor rounded-lg pl-9 text-[#2e3138] outline-none"
                          onKeyDown={(e: React.KeyboardEvent) => {
                            // Запрещаем пробелы и Enter
                            if (e.key === " " || e.key === "Enter") {
                              e.preventDefault();
                            }
                          }}
                          onChange={(e: any) => {
                            formik.handleChange(e);
                            setInitialValues((prevValues: any) => ({
                              ...prevValues,
                              searchTerm: e.target.value,
                            }));
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setIsComplexSearch((prev) => !prev)
                    }
                    }
                    className=" bg-white outline-none text-gray-500 underline flex font-semibold py-2 px-4 rounded min-w-[300px]"
                  >
                    {t("checks.advanced_settings")}
                    <div className="flex flex-col" >
                      <img
                        src={`/icons/arrow${isComplexSearch ? "Down2" : "-top"}.svg`}
                        className={`block h-6 w-6  ${isComplexSearch ? "text-red-500" : ""}`}
                      />
                    </div>
                  </button>
                </div>
                <div className="flex justify-between flex-row gap-[10px]">
                  {resendArray.length > 0
                    ? <button
                      type="button"
                      className="bg-mainColor text-white font-semibold py-2 px-4 h-[40px] rounded whitespace-nowrap"
                      onClick={() => resendCheckList(resendArray)}
                    >
                      {t("checks.resend_selected_checks")}
                    </button>

                    : null
                  }
                  <button
                    type="submit"
                    className="bg-mainColor text-white font-semibold py-2 px-4 h-[40px] rounded whitespace-nowrap"
                  >
                    {t("checks.accept_changes")}
                  </button>

                  <button
                    type="button"
                    className="bg-bg w-auto text-gray-500 font-semibold py-2 px-4 h-[40px] rounded  whitespace-nowrap"

                    onClick={() => {
                      setInitialValues({
                        ofdId: null,
                        fromDateTime: null,
                        toDateTime: null,
                        searchTerm: '',
                        apiVersion: null,
                        sourceType: null,
                      });
                      formik.resetForm({
                        values: {
                          ofdId: null,
                          fromDateTime: null,
                          toDateTime: null,
                          searchTerm: '',
                          apiVersion: null,
                          sourceType: null,
                        },
                      });
                      setDataObject({
                        startDate: undefined, // Начальная дата по умолчанию
                        startTime: "", // Время для начальной даты по умолчанию
                        endDate: undefined, // Конечная дата по умолчанию
                        endTime: "",
                      })
                      setPagination((prevValues: PaginationState) => {
                        return { ...prevValues, pageIndex: 0 };
                      });
                      // setIsFilterOpen(false);
                    }}
                  >
                    {t("checks.clear_filter")}
                  </button>
                </div>
              </div>
              {isComplexSearch ?
                <div className="flex flex-wrap w-full gap-[20px] mt-[20px]">

                  <div className={style.inputField}>
                    <div className={style.labelAndButtonWrapper}>
                      <label className={style.inputLabel2}>
                        {t(
                          "checks.fromTo",
                        )}
                      </label>
                    </div>
                    <CalendarSelectFormik
                      dataObject={dataObject}
                      setDataObject={setDataObject}
                      onChange={() => {

                      }}
                      name="calendar"
                    />
                  </div>

                  <div className={style.inputField}>
                    <div className={style.labelAndButtonWrapper}>
                      <label className={style.inputLabel}>
                        {t("checks.ofd")}
                      </label>
                    </div>
                    <Field
                      value={formik?.values?.ofdId || null}
                      component={SelectCustomFormik}
                      name="ofdId"
                      isClearable={true}
                      // isMulti={true}
                      size="dm"
                      placeholder={t("checks.choose_ofd")}
                      options={ofdList}
                      onChange={(options: any) => {
                        formik.setFieldValue("ofdId", options);

                        setInitialValues((prevValues: any) => {
                          return { ...prevValues, ofdId: options };
                        });
                      }}
                    />
                  </div>

                  <div className={style.inputField}>
                    <div className={style.labelAndButtonWrapper}>
                      <label className={style.inputLabel}>
                        {t("Версия протокола")}
                      </label>
                    </div>
                    <Field
                      value={formik?.values?.apiVersion || null}
                      component={SelectCustomFormik}
                      name="apiVersion"
                      placeholder={t("Выберите версию протокола")}
                      isClearable={true}
                      size="dm"
                      options={[
                        { value: "V_1_0_3", label: "1.0.3" },
                        { value: "V_1_0_4", label: "1.0.4" },
                      ]}
                      onChange={(options: any) => {
                        formik.setFieldValue("apiVersion", options);
                        setInitialValues((prevValues: any) => {
                          return { ...prevValues, apiVersion: options };
                        });
                      }}
                    />
                  </div>

                  <div className={style.inputField}>
                    <div className={style.labelAndButtonWrapper}>
                      <label className={style.inputLabel}>
                        {t("Источник данных")}
                      </label>
                    </div>
                    <Field
                      value={formik?.values?.sourceType || null}
                      component={SelectCustomFormik}
                      name="sourceType"
                      placeholder={t("Выберите источник данных")}
                      isClearable={true}
                      size="dm"
                      options={[
                        { value: "API", label: "API" },
                        { value: "VSHEP", label: "VSHEP" },
                      ]}
                      onChange={(options: any) => {
                        formik.setFieldValue("sourceType", options);
                        setInitialValues((prevValues: any) => {
                          return { ...prevValues, sourceType: options };
                        });
                      }}
                    />
                  </div>
                </div>
                : null
              }   </Form>
          )}
        </Formik>
      </div>

      <div className="w-full">
        <div className="rounded-lg overflow-x-auto bg-white">
          <table className="max-w-full w-full leading-normal">
            <thead className="border-b-2 border-gray-300">
              <tr>
                {/* {tableData?.checks?.length >0 
                ?
                <th
                className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                scope="col"
              >
                <CheckBox
                  checked={
                    resendArray?.length === tableData?.checks?.length
                  }
                  onChange={() => {
                    fullResendMarks();
                  }}
                />
              </th>
                :null
                } */}
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                  scope="col">
                  {/* <CheckBox
                      id="mainCheckbox"
                      checked={resendArray.length>0
                      }
                    /> */}
                  {t("checks.number")}
                </th>

                <th
                  className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                  scope="col"
                >
                  {t("checks.checkId")}
                </th>
                <th
                  className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                  scope="col"
                >
                  {t("checks.uuidCassandra")}
                </th>
                <th
                  className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                  scope="col"
                >
                  {t("checks.iinBin")}
                </th>
                <th
                  className="px-5 py-3 text-center items-center text-gray-600 font-semibold bg-white border-0 border-gray-200 flex justify-center"
                  scope="col"
                >
                  {t("checks.ofd")}
                  <div className="flex flex-col">
                    <button className="h-6 w-6" onClick={toggleSort}>
                      <img
                        src={`/icons/arrow${sort === "ASC" ? "Down2" : "-top"}.svg`}
                        className={`block h-6 w-6  ${sort === "ASC" ? "text-red-500" : ""}`}
                        alt={sort === "ASC" ? "Ascending" : "Descending"}
                      />
                    </button>
                  </div>
                </th>
                <th
                  className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                  scope="col"
                >
                  {t("Версия протокола")}
                </th>
                <th
                  className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                  scope="col"
                >
                  {t("Источник данных")}
                </th>
                <th
                  className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                  scope="col"
                >
                  {t("checks.receiveData")}
                </th>
                <th
                  className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                  scope="col"
                >
                  {t("checks.note")}
                </th>
                <th
                  className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                  scope="col"
                >
                </th>
              </tr>
            </thead>
            {isLoading ? (
              <tbody>
                <tr>
                  <td colSpan={10} className="text-center py-10">
                    <TableLoader />
                  </td>
                </tr>
              </tbody>
            ) : tableData?.checks?.length === 0 || !tableData?.checks ? (
              <tbody>
                <tr>
                  <td colSpan={10} className="text-center py-5">
                    <div>
                      <EmptyListPlaceholderContent
                        title={t("checks.checks_no_found")}
                        description={t("checks.update_page")}
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody className="font-normal text-center">
                {tableData?.checks?.map((value: any, index: any) => (
                  <>
                    <tr
                      key={`tr-${index}-${value.id}`}
                      className="cursor-pointer hover:bg-gray-100"
                      onClick={() => {
                      }}
                    >
                        {/* <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                        <CheckBox
                          id="myCheckbox"
                          checked={resendArray.includes(value?.id)}
                          onChange={() => checkBoxResend(value?.id)}
                        />
                      </td> */}
                      <td className="px-5 py-5 text-sm  border-b border-gray-200 text-gray-600">
                        {/* <CheckBox
                          id={`checkBox${index}`}
                          checked={deleteArray.includes(value.id) || downloadArray.includes(value.id)}
                          // onChange={() => handleCheckboxChange(value.id)}
                        /> */}
                        {calculateItemNumber(index) || "-"}
                      </td>

                      <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                        {value?.checkId || "-"}
                      </td>
                      <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                        {value?.uuidCassandra || "-"}
                      </td>
                      <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                        {value?.iinBin || "-"}
                      </td>
                      <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                        {value?.ofd || "-"}
                      </td>
                      <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                        {formatApiVersion(value?.apiVersion)}
                      </td>
                      <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                        {value?.sourceType || "-"}
                      </td>
                      <td className="px-5 py-5 text-sm  border-b border-gray-200 text-gray-600">
                        {value?.dateReception &&
                          format(value?.dateReception, "dd.MM.yyyy, HH:mm:ss") ||
                          "-"}
                      </td>
                      <td
                        className={style.customTd}
                        data-tooltip-id={`tooltip-${value?.id}`}
                      >
                        <div className="overflow-hidden text-overflow-ellipsis">
                          {value?.note || "-"}
                        </div>
                        <Tooltip
                          id={`tooltip-${value?.id}`}
                          place="top"
                          clickable={true}
                          className={style.customTooltip}
                        >
                          <div className="flex flex-col">
                            <p style={{ margin: "8px 0 0" }}>
                              {value?.note || t("checks.no_note")}
                            </p>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(value?.note || '');
                                handleCopied()
                              }}
                              className="self-center bg-transparent text-gray-400 font-bold py-1 px-3 rounded cursor-pointer flex items-center gap-1 mt-1"
                            >
                              {copied ? (
                                <>
                                  <IoCheckmarkOutline />
                                  {t("checks.copied")}
                                </>
                              ) : (
                                <>
                                  <FaRegCopy />
                                  {t("checks.copy")}
                                </>
                              )}
                            </button>
                          </div>
                        </Tooltip>
                      </td>
                      <td className="px-5 py-5 text-sm  border-b border-gray-200 text-gray-600"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ActionDropwdownCell
                          key={`cell-${index}-${value.id}-actions`}
                          id={value.id}
                          actions={renderActions(
                            value,
                            setErrorModal,
                            navigate,
                          )}
                          activeTooltipId={activeTooltipId}
                          onTooltipToggle={handleTooltipToggle}
                          tooltipRef={tooltipRef}
                        />
                      </td>
                    </tr>

                  </>
                ))}
              </tbody>
            )}
          </table>
        </div>
        <Pagination
          totalItems={tableData?.totalItems || 0}
          totalPages={tableData?.totalPages || 0}
          pagination={pagination}
          setPagination={setPagination}
        />
      </div>

      <ModalComponent
        isOpen={!!(failedModal && failedModal.length > 0)}
        onClose={onModalClose}
        headerContent={
          <h1 className="h__modal">{t("errors.error")}</h1>
        }
        content={
          <p className="p__modal ">
            У следующих документов обнаружен неправильный статус:
            <ul>
              {failedModal?.map((item, index) => (
                <li key={index} className="underline-offset-4">{item}</li>
              ))}
            </ul>

          </p>
        }
        customContent={
          <div className="flex flex-row gap-[10px]">
            <ModalButton
              type="button"
              color="grey"
              onClick={() => {
                setFailedModal([])
              }}
            >
              {t("mainButtons.continue")}
            </ModalButton>

          </div>
        }
      />
      <ModalComponent
        isOpen={checkModal}
        onClose={() => {
          setCheckModal(null);
        }}
        headerContent={
          <h1 className="h__modal">
            {t("checks.check")}
          </h1>
        }
        content={
          <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}>
            {JSON.stringify(formatCheckDataForDisplay(checkModal), null, 2)}
          </pre>
        }
        customContent={

          <ModalButton
            type="button"
            color='green'
            onClick={() => {
              setCheckModal(null);
            }}
          >
            {t("checks.accept")}
          </ModalButton>
        }
      />

      <ModalComponent
        isOpen={errorModal
          // && !errorModal.singleError
        }
        onClose={() => {
          setErrorModal(null);
        }}
        headerContent={
          <h1 className="h__modal">
            {t("notifications.modal_notification")}
          </h1>
        }
        content={
          //   <div className="error-list">
          //   <ul>
          //     {errorModal && errorModal?.length > 0 ? (
          //       errorModal.map((error: any, index: any) => (
          //         <li className="border-b border-gray-300 py-1" key={index}>
          //           {t(`error_dictionary.${error}`)}
          //         </li>
          //       ))
          //     ) : (
          //       <li>{t("documentNotification.no_errors")}</li>
          //     )}
          //   </ul>
          // </div>

          <div className="error-list">
            <ul>
              {Array.isArray(errorModal) && errorModal.length > 0 ? (
                errorModal.map((error: any, index: number) => {
                  console.log("Error data:", error);

                  // Если error — это объект, извлекаем ключи или значения
                  const errorText = typeof error === "object" ? JSON.stringify(error) : error;

                  return (
                    <li className="border-b border-gray-300 py-1" key={index}>
                      Ошибка:  {t(`error_dictionary.${errorText}`)}
                    </li>
                  );
                })
              ) : typeof errorModal === "object" && errorModal !== null ? (
                // Обработка случая, когда errorModal является объектом
                errorModal.errorMessage ? (
                  <li className="border-b border-gray-300 py-1">
                    {t('documentStatus.error')}: {errorModal.errorMessage}
                  </li>
                ) : (
                  <li>{t("notifications.no_errors")}</li>
                )
              ) : (
                <li>{t("notifications.no_errors")}</li>
              )}
            </ul>
          </div>
        }
        customContent={
          <ModalButton
            type="button"
            onClick={() => {
              setErrorModal(null);
            }}
          >
            {t("mainButtons.accept")}
          </ModalButton>
        }
      />
    </div>
  );
};

export default DocumentList;
